import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { User, Minus, Plus } from 'react-feather';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Button,
} from 'reactstrap';

export interface TicketWithSpots {
  id: string;
  title: string;
  spots: number;
}

export interface TicketsSelectorProps {
  tickets: TicketWithSpots[];
  isSingleTicket: boolean;
  maxSpots: number;
  onUpdated: (tickets: TicketWithSpots[]) => void;
  className?: string;
}

export const TicketsSelector: React.FC<TicketsSelectorProps> = ({
  tickets,
  maxSpots,
  onUpdated,
  isSingleTicket,
  className,
}) => {
  const totalCount = useMemo(
    () => tickets.reduce((acc, t) => acc + t.spots, 0),
    [tickets]
  );

  const onCountUpdated = useCallback(
    (id: string, newCount: number) => {
      onUpdated(
        tickets.map((t) => {
          if (t.id === id) {
            return { ...t, spots: Math.max(0, newCount) };
          }
          if (isSingleTicket) {
            return { ...t, spots: 0 };
          }

          return t;
        })
      );
    },
    [isSingleTicket, onUpdated, tickets]
  );

  return (
    <div className={className}>
      {tickets
        .sort((a, b) => a.title.localeCompare(b.title))
        .map(({ id, title, spots }, i) => {
          return (
            <InputGroup
              className={classNames(
                'input-group-merge flex-nowrap overflow-hidden',
                isSingleTicket && 'cursor-pointer',
                i !== 0 && 'mt-50'
              )}
              key={id}
              onClick={
                isSingleTicket && spots < 1
                  ? () => onCountUpdated(id, spots + 1)
                  : undefined
              }
            >
              <InputGroupAddon addonType="prepend" className="flex-grow-1">
                <InputGroupText
                  className="w-100 whitespace-break-spaces text-left"
                  style={
                    isSingleTicket && spots > 0
                      ? {
                          backgroundColor: '#e9eeff',
                        }
                      : undefined
                  }
                >
                  {title}
                </InputGroupText>
              </InputGroupAddon>
              <InputGroupAddon addonType="append">
                <InputGroupText
                  style={
                    isSingleTicket && spots > 0
                      ? {
                          backgroundColor: '#e9eeff',
                        }
                      : undefined
                  }
                >
                  {!isSingleTicket && (
                    <>
                      <Button
                        color="flat-primary"
                        style={{
                          backgroundColor: spots <= 0 ? '#f2f2f2' : '#e9eeff',
                          zIndex: 0,
                        }}
                        onClick={() => onCountUpdated(id, spots - 1)}
                        disabled={spots <= 0}
                        size="sm"
                        className="p-25"
                      >
                        <Minus size={20} />
                      </Button>
                      <div
                        className="d-flex align-items-center justify-content-center px-50"
                        style={{ minWidth: '45px' }}
                      >
                        <User
                          size={16}
                          fill="#7180b7"
                          stroke="#7180b7"
                          strokeLinecap="butt"
                          className="mr-25"
                        />
                        <span className="text-secondary">{spots}</span>
                      </div>
                      <Button
                        color="flat-primary"
                        style={{
                          backgroundColor:
                            spots >= maxSpots ? '#f2f2f2' : '#e9eeff',
                          zIndex: 0,
                        }}
                        className="p-25"
                        onClick={() => onCountUpdated(id, spots + 1)}
                        disabled={totalCount >= maxSpots}
                        size="sm"
                      >
                        <Plus size={20} />
                      </Button>
                    </>
                  )}
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          );
        })}
    </div>
  );
};
